import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, query, where, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import app from '../../firebase';
import CurriculumFlowchart from './CurriculumFlowchart';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';


const db = getFirestore(app);

export default function HeadCurriculum({ department, selectedDepartment, setSelectedDepartment, allDepartments, onBack, initialShowFlowchart = false }) {
  // Local curriculum states (moved from ProgramHeadDashboard)
  const [curriculumYears, setCurriculumYears] = useState([]);
  const [selectedCurriculumYear, setSelectedCurriculumYear] = useState("");
  const [newCurriculumYear, setNewCurriculumYear] = useState("");
  const [curriculumMsg, setCurriculumMsg] = useState("");
  const [semester, setSemester] = useState("First Semester");
  const [yearLevel, setYearLevel] = useState("First Year");
  const [availableYears, setAvailableYears] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [courseName, setCourseName] = useState("");
  const [units, setUnits] = useState("");
  const [frequency, setFrequency] = useState("");
  const [roomTypes, setRoomTypes] = useState([]);
  const [subjectMsg, setSubjectMsg] = useState("");
  const [curriculumSubjects, setCurriculumSubjects] = useState([]);
  const [prerequisite, setPrerequisite] = useState("");
  const [departmentSubjects, setDepartmentSubjects] = useState([]);
  const [departmentCourses, setDepartmentCourses] = useState([]);

  // Modal & editing state
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [subjectInputs, setSubjectInputs] = useState([]);
  const [currentSubjectInput, setCurrentSubjectInput] = useState({ type: 'Lecture', durationHrs: '', durationMins: '', roomType: '' });
  const [editingRowIds, setEditingRowIds] = useState(null);
  const [editingSubjectIndex, setEditingSubjectIndex] = useState(null);

  // Table and filters
  const [curriculumPage, setCurriculumPage] = useState(1);
  const rowsPerPage = 20;
  const [filterSemesterTable, setFilterSemesterTable] = useState("");
  const [filterYearLevelTable, setFilterYearLevelTable] = useState("");
  const [showFlowchart, setShowFlowchart] = useState(initialShowFlowchart || false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [rowDeleteError, setRowDeleteError] = useState('');

  useEffect(() => {
    if (initialShowFlowchart) {
      setShowFlowchart(true);
    }
  }, [initialShowFlowchart]);

  // Simple helpers preserved
  const parseDurationStr = (dur) => {
    if (!dur || typeof dur !== 'string') return { hrs: '', mins: '' };
    const m = /(?:(\d+)\s*hrs?)?\s*(?:(\d+)\s*mins?)?/.exec(dur);
    if (!m) return { hrs: '', mins: '' };
    return { hrs: m[1] || '', mins: m[2] || '' };
  };

  const isDuplicateCourse = (cid, cname, excludeIds = []) => {
    if (!cid && !cname) return false;
    const cidNorm = (cid || '').toString().trim().toLowerCase();
    const cnameNorm = (cname || '').toString().trim().toLowerCase();
    return departmentSubjects.some(s => {
      if (excludeIds && excludeIds.includes(s.id)) return false;
      const sid = (s.courseId || '').toString().trim().toLowerCase();
      const sname = (s.courseName || '').toString().trim().toLowerCase();
      return (cidNorm && sid === cidNorm) || (cnameNorm && sname === cnameNorm);
    });
  };

  // Normalize input to a subject code (prefer matching department courseId)
  const normalizePrerequisite = (input) => {
    if (!input) return "";
    const p = input.toString().trim();
    // exact courseId match
    let found = departmentCourses.find(c => (c.courseId || '').toLowerCase() === p.toLowerCase());
    if (found) return found.courseId;
    // left side of " - " e.g. "BIO 100 - Biochemistry"
    if (p.includes(' - ')) {
      const left = p.split(' - ')[0].trim();
      found = departmentCourses.find(c => (c.courseId || '').toLowerCase() === left.toLowerCase());
      return (found ? found.courseId : left);
    }
    // contains a known courseId substring
    found = departmentCourses.find(c => p.toLowerCase().includes((c.courseId || '').toLowerCase()));
    if (found) return found.courseId;
    // try simple pattern match like "BIO100" or "BIO 100"
    const m = p.match(/[A-Za-z]{2,}\s*\d{2,}/);
    return m ? m[0].replace(/\s+/, ' ').toUpperCase() : "";
  };

  // Fetch curriculum years
  useEffect(() => {
    const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
    if (!dep) return;
    const fetchCurriculumYears = async () => {
      const q = query(collection(db, "curriculums"), where("department", "==", dep));
      const snapshot = await getDocs(q);
      setCurriculumYears(snapshot.docs.map(doc => doc.data().year));
    };
    fetchCurriculumYears();
  }, [department, selectedDepartment, curriculumMsg]);

  // Load department years
  useEffect(() => {
    const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
    if (!dep) {
      setAvailableYears([]);
      return;
    }
    const fetchDeptYears = async () => {
      try {
        const docSnap = await getDocs(query(collection(db, 'departments'), where('name', '==', dep)));
        const docs = docSnap.docs.map(d => d.data());
        if (docs.length > 0 && Array.isArray(docs[0].years)) setAvailableYears(docs[0].years);
      } catch (err) {
        // keep default
      }
    };
    fetchDeptYears();
  }, [department, selectedDepartment, curriculumMsg]);

  // Fetch room types
  useEffect(() => {
    const fetchRoomTypes = async () => {
      const snapshot = await getDocs(collection(db, "rooms"));
      const types = [...new Set(snapshot.docs.map(doc => doc.data().roomType))].filter(Boolean);
      setRoomTypes(types);
    };
    fetchRoomTypes();
  }, []);

  // Fetch curriculum subjects
  useEffect(() => {
    const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
    if (!dep) return;
    if (!selectedCurriculumYear && !initialShowFlowchart) return;

    const fetchSubjects = async () => {
      const filters = [where("department", "==", dep)];
      if (selectedCurriculumYear) {
        filters.push(where("year", "==", selectedCurriculumYear));
      }
      const q = query(collection(db, "curriculum_subjects"), ...filters);
      const snapshot = await getDocs(q);
      const subjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCurriculumSubjects(subjects);
    };
    fetchSubjects();
  }, [department, selectedDepartment, selectedCurriculumYear, subjectMsg, initialShowFlowchart]);

  // Fetch all subjects for this department (prereq & duplicates)
  useEffect(() => {
    const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
    if (!dep) {
      setDepartmentSubjects([]);
      return;
    }
    const fetchAllDeptSubjects = async () => {
      const q = query(collection(db, "curriculum_subjects"), where("department", "==", dep));
      const snap = await getDocs(q);
      const docs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDepartmentSubjects(docs);
      const uniqMap = docs.reduce((acc, s) => {
        const key = (s.courseId || s.courseName || '').toString().trim().toLowerCase();
        if (!acc[key]) acc[key] = { courseId: s.courseId, courseName: s.courseName };
        return acc;
      }, {});
      setDepartmentCourses(Object.values(uniqMap));
    };
    fetchAllDeptSubjects();
  }, [department, selectedDepartment, subjectMsg]);

  // Grouping logic (copied from original for stability)
  const groupedCurriculumRows = useMemo(() => {
    return Object.values(
      curriculumSubjects.reduce((acc, subj) => {
        // group by normalized prerequisite code so table shows subject code only
        const prereqCode = normalizePrerequisite(subj.prerequisite || "");
        const key = [subj.semester, subj.yearLevel, subj.courseId, subj.courseName, subj.units, prereqCode].join("|");
        if (!acc[key]) acc[key] = { semester: subj.semester, yearLevel: subj.yearLevel, courseId: subj.courseId, courseName: subj.courseName, units: subj.units, prerequisite: prereqCode, ids: [], lecture: null, laboratory: null, internship: null };

        if (subj.type) {
          acc[key].ids = acc[key].ids.concat(subj.id);
        }

        if (subj.lectureSessions) {
          acc[key].lecture = { sessions: subj.lectureSessions.map(s => ({ duration: s.duration, roomType: s.roomType, prerequisite: s.prerequisite, addedByDepartment: subj.addedByDepartment })) };
        }
        if (subj.laboratorySessions) {
          acc[key].laboratory = { sessions: subj.laboratorySessions.map(s => ({ duration: s.duration, roomType: s.roomType, prerequisite: s.prerequisite, addedByDepartment: subj.addedByDepartment })) };
        }
        if (subj.internshipSessions) {
          acc[key].internship = { sessions: subj.internshipSessions.map(s => ({ duration: s.duration, roomType: s.roomType, prerequisite: s.prerequisite, addedByDepartment: subj.addedByDepartment })) };
        }

        if (subj.lecture && !acc[key].lecture) acc[key].lecture = { duration: subj.lecture.duration, roomType: subj.lecture.roomType, prerequisite: subj.lecture.prerequisite, addedByDepartment: subj.lecture.addedByDepartment };
        if (subj.laboratory && !acc[key].laboratory) acc[key].laboratory = { duration: subj.laboratory.duration, roomType: subj.laboratory.roomType, prerequisite: subj.laboratory.prerequisite, addedByDepartment: subj.laboratory.addedByDepartment };
        if (subj.internship && !acc[key].internship) acc[key].internship = { duration: subj.internship.duration, roomType: subj.internship.roomType, prerequisite: subj.internship.prerequisite, addedByDepartment: subj.internship.addedByDepartment };

        if (!acc[key].ids.includes(subj.id)) acc[key].ids.push(subj.id);

        return acc;
      }, {})
    );
  }, [curriculumSubjects, departmentCourses]);

  const filteredCurriculumRows = groupedCurriculumRows.filter(r => {
    return (filterSemesterTable ? r.semester === filterSemesterTable : true) && (filterYearLevelTable ? r.yearLevel === filterYearLevelTable : true);
  });

  // Row handlers
  const handleEditRow = (row) => {
    setSubjectMsg('');
    setCourseId(row.courseId || '');
    setCourseName(row.courseName || '');
    setUnits(row.units || '');
    setFrequency(row.frequency || '');
    setSemester(row.semester || 'First Semester');
    setYearLevel(row.yearLevel || 'First Year');
    if (row.year) setSelectedCurriculumYear(row.year);
    const inputs = [];
    if (row.lecture) {
      if (Array.isArray(row.lecture.sessions) && row.lecture.sessions.length > 0) {
        for (const s of row.lecture.sessions) inputs.push({ type: 'Lecture', durationHrs: parseDurationStr(s.duration).hrs, durationMins: parseDurationStr(s.duration).mins, roomType: s.roomType, id: s.id });
      } else inputs.push({ type: 'Lecture', durationHrs: parseDurationStr(row.lecture.duration).hrs, durationMins: parseDurationStr(row.lecture.duration).mins, roomType: row.lecture.roomType });
    }
    if (row.laboratory) {
      if (Array.isArray(row.laboratory.sessions) && row.laboratory.sessions.length > 0) {
        for (const s of row.laboratory.sessions) inputs.push({ type: 'Laboratory', durationHrs: parseDurationStr(s.duration).hrs, durationMins: parseDurationStr(s.duration).mins, roomType: s.roomType, id: s.id });
      } else inputs.push({ type: 'Laboratory', durationHrs: parseDurationStr(row.laboratory.duration).hrs, durationMins: parseDurationStr(row.laboratory.duration).mins, roomType: row.laboratory.roomType });
    }
    if (row.internship) {
      if (Array.isArray(row.internship.sessions) && row.internship.sessions.length > 0) {
        for (const s of row.internship.sessions) inputs.push({ type: 'Internship', durationHrs: parseDurationStr(s.duration).hrs, durationMins: parseDurationStr(s.duration).mins, roomType: s.roomType, id: s.id });
      } else inputs.push({ type: 'Internship', durationHrs: parseDurationStr(row.internship.duration).hrs, durationMins: parseDurationStr(row.internship.duration).mins, roomType: row.internship.roomType });
    }
    setSubjectInputs(inputs);
    setCurrentSubjectInput({ type: 'Lecture', durationHrs: '', durationMins: '', roomType: roomTypes.length > 0 ? roomTypes[0] : '' });
    setPrerequisite((row.prerequisite && row.prerequisite) || (row.lecture && row.lecture.prerequisite) || (row.laboratory && row.laboratory.prerequisite) || "");
    setEditingRowIds(row.ids);
    setShowSubjectModal(true);
  };

  const handleDeleteRow = (row) => {
    setRowDeleteError('');
    setRowToDelete(row);
  };

  const confirmDeleteRow = async () => {
    if (!rowToDelete) return;
    try {
      for (const id of (rowToDelete.ids || [])) {
        await deleteDoc(doc(db, 'curriculum_subjects', id));
      }
      setSubjectMsg('Subject and its entries deleted!');
      setRowToDelete(null);
    } catch (err) {
      setRowDeleteError(err.message || 'Error deleting subject');
    }
  };

  // Add curriculum year
  const handleAddCurriculumYear = async (e) => {
    e.preventDefault();
    setCurriculumMsg("");
    if (!newCurriculumYear) {
      setCurriculumMsg("Please enter a curriculum year.");
      return;
    }
    
    // Normalize: remove all spaces around the dash
    const normalizedYear = newCurriculumYear.trim().replace(/\s*-\s*/g, '-');
    
    // Validate year format (YYYY-YYYY)
    const yearRegex = /^\d{4}-\d{4}$/;
    if (!yearRegex.test(normalizedYear)) {
      setCurriculumMsg("Please enter a valid curriculum year format (e.g., 2018-2019).");
      return;
    }
    
    // Extract start and end years
    const [startYearStr, endYearStr] = normalizedYear.split('-');
    const startYear = parseInt(startYearStr, 10);
    const endYear = parseInt(endYearStr, 10);
    
    // Validate that end year is exactly 1 year after start year
    if (endYear !== startYear + 1) {
      setCurriculumMsg("The end year must be exactly one year after the start year (e.g., 2018-2019, not 2018-2020).");
      return;
    }
    
    // Check if year already exists in curriculum years (case-insensitive, handling spacing variations)
    const yearExists = curriculumYears.some(year => 
      year.replace(/\s*-\s*/g, '-').toLowerCase() === normalizedYear.toLowerCase()
    );
    if (yearExists) {
      setCurriculumMsg(`The curriculum year ${normalizedYear} already exists for this department.`);
      return;
    }
    
    const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
    try {
      await addDoc(collection(db, "curriculums"), { department: dep, year: normalizedYear });
      setCurriculumMsg("Curriculum year added!");
      setNewCurriculumYear("");
    } catch (err) {
      setCurriculumMsg(err.message);
    }
  };



  const handleVerifySubject = (e) => {
    e.preventDefault();
    setSubjectMsg("");
    if (!selectedCurriculumYear) {
      setSubjectMsg("Please select a curriculum year.");
      return;
    }
    setSubjectInputs([]);
    setCurrentSubjectInput({ type: "Lecture", durationHrs: "", durationMins: "", roomType: roomTypes.length > 0 ? roomTypes[0] : "" });
    if (isDuplicateCourse(courseId, courseName)) {
      setSubjectMsg("A subject with the same Course ID or Course Name already exists in this department.");
      return;
    }
    setShowSubjectModal(true);
  };

  const handleAddLabLec = () => {
    if (currentSubjectInput.durationHrs && currentSubjectInput.durationMins && currentSubjectInput.roomType) {
      if (editingSubjectIndex !== null && editingSubjectIndex >= 0) {
        setSubjectInputs(prev => prev.map((v, i) => i === editingSubjectIndex ? { ...currentSubjectInput } : v));
        setEditingSubjectIndex(null);
      } else {
        setSubjectInputs(prev => [...prev, { ...currentSubjectInput }]);
      }
      setCurrentSubjectInput({ type: currentSubjectInput.type, durationHrs: "", durationMins: "", roomType: roomTypes.length > 0 ? roomTypes[0] : "" });
    } else {
      alert("Please fill in all fields for the current Lecture/Laboratory entry before adding or updating.");
    }
  };

  const editSubjectInput = (index) => {
    const item = subjectInputs[index];
    if (!item) return;
    setCurrentSubjectInput({ ...item });
    setEditingSubjectIndex(index);
  };

  const removeSubjectInput = (index) => {
    setSubjectInputs(prev => prev.filter((_, i) => i !== index));
    if (editingSubjectIndex === index) {
      setEditingSubjectIndex(null);
      setCurrentSubjectInput({ type: 'Lecture', durationHrs: '', durationMins: '', roomType: roomTypes.length > 0 ? roomTypes[0] : '' });
    }
  };

  const handleSaveSubjectWithTypes = async () => {
    try {
      let finalSubjectInputs = [...subjectInputs];
      if (currentSubjectInput.durationHrs && currentSubjectInput.durationMins && currentSubjectInput.roomType) finalSubjectInputs.push({ ...currentSubjectInput });
      const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
      if (isDuplicateCourse(courseId, courseName, editingRowIds || [])) {
        setSubjectMsg("A subject with the same Course ID or Course Name already exists in this department.");
        return;
      }
      if (editingRowIds && Array.isArray(editingRowIds) && editingRowIds.length > 0) {
        for (const id of editingRowIds) {
          await deleteDoc(doc(db, 'curriculum_subjects', id));
        }
      }
      const lectureSessions = [];
      const laboratorySessions = [];
      const internshipSessions = [];
      for (const input of finalSubjectInputs) {
        const dur = `${input.durationHrs ? input.durationHrs + ' hrs' : ''}${input.durationMins ? ' ' + input.durationMins + ' mins' : ''}`.trim();
        if (input.type === 'Lecture') lectureSessions.push({ duration: dur, roomType: input.roomType, prerequisite });
        else if (input.type === 'Internship') internshipSessions.push({ duration: dur, roomType: input.roomType, prerequisite });
        else laboratorySessions.push({ duration: dur, roomType: input.roomType, prerequisite });
      }
      const subjectDoc = { department: dep, year: selectedCurriculumYear, semester, yearLevel, courseId, courseName, units, frequency, prerequisite: prerequisite || "", addedByDepartment: dep };
      if (lectureSessions.length > 0) subjectDoc.lectureSessions = lectureSessions;
      if (laboratorySessions.length > 0) subjectDoc.laboratorySessions = laboratorySessions;
      if (internshipSessions.length > 0) subjectDoc.internshipSessions = internshipSessions;
      await addDoc(collection(db, 'curriculum_subjects'), subjectDoc);
      setSubjectMsg(editingRowIds ? 'Subject updated!' : 'Subject added!');
      setPrerequisite('');
      setEditingRowIds(null);
      setCourseId('');
      setCourseName('');
      setUnits('');
      setFrequency('');
      setShowSubjectModal(false);
      setSubjectInputs([]);
    } catch (err) {
      setSubjectMsg(err.message || 'Error saving subject');
    }
  };

  if (initialShowFlowchart) {
    const dep = department.toLowerCase().includes("general") ? selectedDepartment : department;
    const flowDepartment = dep || department;
    return (
      <div style={{ maxWidth: 1800, margin: "40px auto", padding: 32, border: "1px solid #ccc", borderRadius: 12, background: '#fff' }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0 }}>Curriculum Flowchart</h2>
            <div style={{ color: '#555', marginTop: 6 }}>Department: {flowDepartment || 'N/A'}</div>
          </div>
          <button onClick={onBack} style={{ background: '#155c2b', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 18px', cursor: 'pointer', fontWeight: 600 }}>Back</button>
        </div>
        <CurriculumFlowchart subjects={curriculumSubjects} department={flowDepartment} yearLabel="All Years" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1800, margin: "40px auto", padding: 32, border: "1px solid #ccc", borderRadius: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Curriculum Management</h2>
        
      </div>

      <div style={{ marginBottom: 24 }}>
        <b>Department:</b> {department}
        {department.toLowerCase().includes("general") && (
          <span style={{ marginLeft: 16 }}>
            <b>Selected Department:</b>
            <select value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)} style={{ marginLeft: 8, padding: 8 }}>
              {allDepartments.map(dep => <option key={dep} value={dep}>{dep}</option>)}
            </select>
          </span>
        )}
      </div>

      <div style={{ marginBottom: 48 }}>
        <h3>Curriculum Years for {department}</h3>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <select value={selectedCurriculumYear} onChange={e => setSelectedCurriculumYear(e.target.value)} style={{ padding: 10, minWidth: 220 }}>
            <option value="">Select Curriculum Year</option>
            {curriculumYears.map((year, idx) => <option key={idx} value={year}>{year}</option>)}
          </select>
          <span>or</span>
          <form onSubmit={handleAddCurriculumYear} style={{ display: "inline" }}>
            <input type="text" placeholder="New Curriculum Year (e.g. 2018-2019)" value={newCurriculumYear} onChange={e => setNewCurriculumYear(e.target.value)} style={{ padding: 10, minWidth: 300 }} />
            <button type="submit" style={{  background: "#155c2b", color: "#fff", padding: "10px 20px", marginLeft: 8 }}>Add</button>
          </form>
        </div>
        {curriculumMsg && <div style={{ color: curriculumMsg.includes("added") ? "green" : "red", marginTop: 10 }}>{curriculumMsg}</div>}

      </div>

      {selectedCurriculumYear && (
        <div style={{ marginBottom: 48 }}>
          <h3>Curriculum of {department} ({selectedCurriculumYear})</h3>

          <form onSubmit={handleVerifySubject} style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <select value={semester} onChange={e => setSemester(e.target.value)} style={{ padding: 10, minWidth: 180 }}>
              <option>First Semester</option>
              <option>Second Semester</option>
            </select>
            <select value={yearLevel} onChange={e => setYearLevel(e.target.value)} style={{ padding: 10, minWidth: 180 }}>
              {availableYears && availableYears.length > 0 ? (
                availableYears.map((y, idx) => <option key={idx} value={y}>{y}</option>)
              ) : (
                <>
                  <option>First Year</option>
                  <option>Second Year</option>
                  <option>Third Year</option>
                  <option>Fourth Year</option>
                </>
              )}
            </select>
            <input type="text" placeholder="Course ID" value={courseId} onChange={e => setCourseId(e.target.value)} style={{ padding: 10, minWidth: 180 }} required />
            <input type="text" placeholder="Course Name" value={courseName} onChange={e => setCourseName(e.target.value)} style={{ padding: 10, minWidth: 180 }} required />
            <input type="number" placeholder="Units" value={units} onChange={e => setUnits(e.target.value)} style={{ padding: 10, width: 150 }} required />
            <input type="number" placeholder="Frequency (per week)" value={frequency} onChange={e => setFrequency(e.target.value)} style={{ padding: 10, width: 150 }} required />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input list="prereq-list" placeholder="Prerequisite (optional — enter course code)" value={prerequisite} onChange={e => setPrerequisite(e.target.value)} style={{ padding: 10, minWidth: 352 }} />
              <datalist id="prereq-list">
                {departmentCourses.map((c, idx) => <option key={idx} value={c.courseId} />)}
              </datalist>
            </div>

            <button type="submit" style={{  background: "#155c2b", color: "#fff", minWidth: 805, padding: "10px 20px" }}>Add Subject Details</button>
          </form>
          {subjectMsg && <div style={{ color: subjectMsg.includes("added") ? "green" : "red", marginTop: 10 }}>{subjectMsg}</div>}

          {showSubjectModal && (
            <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
              <div style={{ background: "#fff", padding: 32, borderRadius: 12, minWidth: 400 }}>
                <h3>Verify Subject Details</h3>
                {subjectMsg && <div style={{ color: subjectMsg.includes("added") ? "green" : "red", marginTop: 8 }}>{subjectMsg}</div>}
                {/* Modal content copied from original for parity */}
                <div>
                  {editingRowIds ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                      {/* ... inputs omitted for brevity - preserved in original file */}
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
                        <label style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Course ID</label>
                        <input type="text" placeholder="Course ID" value={courseId} onChange={e => setCourseId(e.target.value)} style={{ padding: 8, minWidth: 120 }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                        <label style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Course Name</label>
                        <input type="text" placeholder="Course Name" value={courseName} onChange={e => setCourseName(e.target.value)} style={{ padding: 8, minWidth: 220 }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 80 }}>
                        <label style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Units</label>
                        <input type="number" placeholder="Units" value={units} onChange={e => setUnits(e.target.value)} style={{ padding: 8, width: 80 }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 150 }}>
                        <label style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Frequency</label>
                        <input type="number" placeholder="Frequency (per week)" value={frequency} onChange={e => setFrequency(e.target.value)} style={{ padding: 8, width: 150 }} min={0} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Semester</label>
                        <select value={semester} onChange={e => setSemester(e.target.value)} style={{ padding: 8 }}>
                          <option>First Semester</option>
                          <option>Second Semester</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Year Level</label>
                        <select value={yearLevel} onChange={e => setYearLevel(e.target.value)} style={{ padding: 8 }}>
                          {availableYears && availableYears.length > 0 ? (
                            availableYears.map((y, idx) => <option key={idx} value={y}>{y}</option>)
                          ) : (
                            <>
                              <option>First Year</option>
                              <option>Second Year</option>
                              <option>Third Year</option>
                              <option>Fourth Year</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <>
                      <b>Course ID:</b> {courseId}<br />
                      <b>Course Name:</b> {courseName}<br />
                      <b>Units:</b> {units}<br />
                      <b>Frequency:</b> {frequency}<br />
                      <b>Semester:</b> {semester}<br />
                      <b>Year Level:</b> {yearLevel}<br />
                    </>
                  )}
                  <hr />
                  <div>
                    <h4>Lecture/Laboratory/Internship Details</h4>
                    {subjectInputs.map((input, idx) => (
                      <div key={idx} style={{ marginBottom: 8, padding: 8, border: "1px solid #eee", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <b>{input.type}:</b>{' '}
                          {(input.durationHrs || input.durationMins || input.roomType) ? (
                            <>
                              {input.durationHrs ? `${input.durationHrs} hrs` : null}
                              {input.durationMins ? ` ${input.durationMins} mins` : null}
                              {input.roomType ? `${(input.durationHrs || input.durationMins) ? ', ' : ''}Room: ${input.roomType}` : null}
                            </>
                          ) : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" onClick={() => editSubjectInput(idx)} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #007bff', background: '#fff', color: '#007bff', cursor: 'pointer' }}>Edit</button>
                          <button type="button" onClick={() => removeSubjectInput(idx)} style={{ padding: '6px 10px', borderRadius: 4, border: 'none', background: '#dc3545', color: '#fff', cursor: 'pointer' }}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div style={{ marginBottom: 8, padding: 8, border: "1px solid #eee" }}>
                      <select value={currentSubjectInput.type} onChange={e => setCurrentSubjectInput({ ...currentSubjectInput, type: e.target.value })} style={{ marginRight: 8 }}>
                        <option value="Lecture">Lecture</option>
                        <option value="Laboratory">Laboratory</option>
                        <option value="Internship">Internship</option>
                      </select>
                      <input type="number" placeholder="Duration (hrs)" value={currentSubjectInput.durationHrs} onChange={e => setCurrentSubjectInput({ ...currentSubjectInput, durationHrs: e.target.value })} style={{ width: 80, marginRight: 8 }} />
                      <input type="number" placeholder="Duration (mins)" value={currentSubjectInput.durationMins} onChange={e => setCurrentSubjectInput({ ...currentSubjectInput, durationMins: e.target.value })} style={{ width: 90, marginRight: 8 }} />
                      <select value={currentSubjectInput.roomType} onChange={e => setCurrentSubjectInput({ ...currentSubjectInput, roomType: e.target.value })} style={{ minWidth: 120, marginRight: 8 }}>
                        <option value="">Select Room Type</option>
                        {roomTypes.map((type, idx) => <option key={idx} value={type}>{type}</option>)}
                      </select>
                      <button type="button" onClick={handleAddLabLec} style={{ padding: "6px 12px", marginRight: 8 }}>
                        {editingSubjectIndex !== null ? 'Update Entry' : 'Add Entry'}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, justifyContent: "flex-end", marginTop: 16 }}>
                    <button onClick={() => setShowSubjectModal(false)}>Cancel</button>
                    <button onClick={handleSaveSubjectWithTypes} style={{ background: "#007bff", color: "#fff", padding: "8px 18px", border: "none", borderRadius: 4 }}>Save All Subject Types</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={{ padding: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 8, background: '#f8fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <select value={filterSemesterTable} onChange={e => { setFilterSemesterTable(e.target.value); setCurriculumPage(1); }} style={{ padding: 8 }}>
                <option value="">All Semesters</option>
                <option>First Semester</option>
                <option>Second Semester</option>
              </select>
              <select value={filterYearLevelTable} onChange={e => { setFilterYearLevelTable(e.target.value); setCurriculumPage(1); }} style={{ padding: 8 }}>
                <option value="">All Year Levels</option>
                {Array.from(new Set(groupedCurriculumRows.map(r => r.yearLevel))).map((y, i) => <option key={i} value={y}>{y}</option>)}
              </select>
              <button onClick={() => { setFilterSemesterTable(''); setFilterYearLevelTable(''); setCurriculumPage(1); }} style={{ padding: '6px 12px' }}>Clear Filters</button>
            </div>
          </div>

          <div style={{ overflowX: "auto", marginTop: 0, paddingBottom: 40, WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", minWidth: 900, tableLayout: "fixed", borderCollapse: "collapse", background: "#fff", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9f9f9" }}>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 100, whiteSpace: 'nowrap', fontSize: 12 }}>Semester</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 100, whiteSpace: 'nowrap', fontSize: 12 }}>Year Level</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 100, whiteSpace: 'nowrap', fontSize: 12 }}>Course ID</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 280, fontSize: 12 }}>Course Name</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 60, whiteSpace: 'nowrap', fontSize: 12 }}>Units</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 150, fontSize: 12 }}>Prerequisite</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, fontSize: 12 }}>Lecture</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, fontSize: 12 }}>Laboratory</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, fontSize: 12 }}>Internship</th>
                  <th style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", fontWeight: 600, width: 110, fontSize: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCurriculumRows.slice((curriculumPage - 1) * rowsPerPage, curriculumPage * rowsPerPage).map((row, idx) => (
                  <tr key={row.ids.join("-")} style={{ height: 40 }}> 
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "center", width: 100, whiteSpace: 'nowrap', fontSize: 13 }}>{row.semester}</td>
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "center", width: 100, whiteSpace: 'nowrap', fontSize: 13 }}>{row.yearLevel}</td>
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "center", width: 100, whiteSpace: 'nowrap', fontSize: 13 }}>{row.courseId}</td>
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "left", maxWidth: 280, width: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>{row.courseName}</td>
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "center", width: 60, fontSize: 13 }}>{row.units}</td>
                    {/* show prerequisite instead of frequency in table */}
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "center", width: 150, fontSize: 12 }}>{row.prerequisite || ""}</td>
                    <td style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", minWidth: 180, maxWidth: 240, wordBreak: "break-word", fontSize: 12 }}>
                      {(() => {
                        const lectureCount = row.lecture ? (Array.isArray(row.lecture.sessions) ? row.lecture.sessions.length : (row.lecture.duration || row.lecture.roomType) ? 1 : 0) : 0;
                        if (lectureCount > 0) {
                          return (
                            <div>
                              {Array.isArray(row.lecture.sessions) ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {row.lecture.sessions.map((s, i) => (
                                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <div style={{ fontWeight: 600 }}>{s.duration || <span style={{ color: "#888" }}>N/A</span>}</div>
                                      <div style={{ color: "#555", fontSize: 13 }}>{s.roomType ? `Room: ${s.roomType}` : <span style={{ color: "#888" }}>N/A</span>}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  <div style={{ fontWeight: 600 }}>{row.lecture.duration || <span style={{ color: "#888" }}>N/A</span>}</div>
                                  <div style={{ color: "#555", fontSize: 13 }}>{row.lecture.roomType ? `Room: ${row.lecture.roomType}` : <span style={{ color: "#888" }}>N/A</span>}</div>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <span style={{ color: "#888" }}>N/A</span>
                        );
                      })()}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", minWidth: 180, maxWidth: 240, wordBreak: "break-word", fontSize: 12 }}>
                      {(() => {
                        const labCount = row.laboratory ? (Array.isArray(row.laboratory.sessions) ? row.laboratory.sessions.length : (row.laboratory.duration || row.laboratory.roomType) ? 1 : 0) : 0;
                        if (labCount > 0) {
                          return (
                            <div>
                              {Array.isArray(row.laboratory.sessions) ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {row.laboratory.sessions.map((s, i) => (
                                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <div style={{ fontWeight: 600 }}>{s.duration || <span style={{ color: "#888" }}>N/A</span>}</div>
                                      <div style={{ color: "#555", fontSize: 13 }}>{s.roomType ? `Room: ${s.roomType}` : <span style={{ color: "#888" }}>N/A</span>}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  <div style={{ fontWeight: 600 }}>{row.laboratory.duration || <span style={{ color: "#888" }}>N/A</span>}</div>
                                  <div style={{ color: "#555", fontSize: 13 }}>{row.laboratory.roomType ? `Room: ${row.laboratory.roomType}` : <span style={{ color: "#888" }}>N/A</span>}</div>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <span style={{ color: "#888" }}>N/A</span>
                        );
                      })()}
                    </td>

                    <td style={{ border: "1px solid #ccc", padding: '4px 6px', textAlign: "center", minWidth: 180, maxWidth: 240, wordBreak: "break-word", fontSize: 12 }}>
                      {(() => {
                        const internshipCount = row.internship ? (Array.isArray(row.internship.sessions) ? row.internship.sessions.length : (row.internship.duration || row.internship.roomType) ? 1 : 0) : 0;
                        if (internshipCount > 0) {
                          return (
                            <div>
                              {Array.isArray(row.internship.sessions) ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {row.internship.sessions.map((s, i) => (
                                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      <div style={{ fontWeight: 600 }}>{s.duration || <span style={{ color: "#888" }}>N/A</span>}</div>
                                      <div style={{ color: "#555", fontSize: 13 }}>{s.roomType ? `Room: ${s.roomType}` : <span style={{ color: "#888" }}>N/A</span>}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  <div style={{ fontWeight: 600 }}>{row.internship.duration || <span style={{ color: "#888" }}>N/A</span>}</div>
                                  <div style={{ color: "#555", fontSize: 13 }}>{row.internship.roomType ? `Room: ${row.internship.roomType}` : <span style={{ color: "#888" }}>N/A</span>}</div>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <span style={{ color: "#888" }}>N/A</span>
                        );
                      })()}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: '3px 4px', textAlign: "center", position: "relative", width: 110 }}>
                      {(() => {
                        const lectureCount = row.lecture ? (Array.isArray(row.lecture.sessions) ? row.lecture.sessions.length : (row.lecture.duration || row.lecture.roomType) ? 1 : 0) : 0;
                        const labCount = row.laboratory ? (Array.isArray(row.laboratory.sessions) ? row.laboratory.sessions.length : (row.laboratory.duration || row.laboratory.roomType) ? 1 : 0) : 0;
                        const internshipCount = row.internship ? (Array.isArray(row.internship.sessions) ? row.internship.sessions.length : (row.internship.duration || row.internship.roomType) ? 1 : 0) : 0;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                              <button style={{ padding: '3px 8px', borderRadius: 3, border: '1px solid #007bff', background: '#fff', color: '#007bff', cursor: 'pointer', fontSize: 12 }} onClick={() => handleEditRow(row)}>Edit</button>
                              <button style={{ padding: '3px 8px', borderRadius: 3, border: 'none', background: '#dc3545', color: '#fff', cursor: 'pointer', fontSize: 12 }} onClick={() => handleDeleteRow(row)}>Delete</button>
                            </div>
                            {lectureCount === 0 && labCount === 0 && internshipCount === 0 && (
                              <button onClick={() => handleEditRow(row)} style={{ padding: '2px 6px', fontSize: 11, marginTop: 2, background: '#eef', border: '1px solid #d7eafc', borderRadius: 3, cursor: 'pointer' }}>Add Details</button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, textAlign: "center" }}>
            {Array.from({ length: Math.ceil(filteredCurriculumRows.length / rowsPerPage) }).map((_, i) => (
              <button key={i} onClick={() => setCurriculumPage(i + 1)} style={{ margin: "0 4px", padding: "6px 12px", background: curriculumPage === i + 1 ? "#007bff" : "#fff", color: curriculumPage === i + 1 ? "#fff" : "#007bff", border: "1px solid #007bff", borderRadius: 4, cursor: "pointer" }}>{i + 1}</button>
            ))}
          </div>

          {/* Flowchart visualization for the selected curriculum year */}
          <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowFlowchart(prev => !prev)}
              style={{
                background: showFlowchart ? '#dc3545' : '#155c2b',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 18px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {showFlowchart ? 'Hide Curriculum Flowchart' : 'Show Curriculum Flowchart'}
            </button>
            {showFlowchart && (
              <div style={{ width: '100%' }}>
                <CurriculumFlowchart subjects={curriculumSubjects} department={department} yearLabel={selectedCurriculumYear} />
              </div>
            )}
          </div>

        </div>
      )}
      <DeleteConfirmationModal
        open={Boolean(rowToDelete)}
        title="Delete Curriculum Subject?"
        subtitle="Confirm curriculum deletion"
        name={rowToDelete?.courseName || "this subject"}
        message={`Are you sure you want to delete ${rowToDelete?.courseName || "this subject"} and all its Lecture, Laboratory, and Internship entries? This action cannot be undone.`}
        detail="Please verify the curriculum details before proceeding."
        error={rowDeleteError}
        confirmLabel="Delete Subject"
        onCancel={() => { setRowToDelete(null); setRowDeleteError(''); }}
        onConfirm={confirmDeleteRow}
      />
    </div>
  );
}